import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { EnlargedImageCarousel } from "./enlarged-image-carousel.component";
import { PictureDropzone2 } from "../../components/Pickers/picture-dropzone.component(2)";
import { FileObject } from "../FileDropzone/typings/FileObject.type";

interface Props {
  handleDelete: (picturePath: string) => void;
  isOpen: boolean;
  isLoading: boolean;
  picturePaths: string[] | null;
  files: FileObject[];
  setFiles: React.Dispatch<React.SetStateAction<FileObject[]>>;
  picturePathsToDelete: string[];
  saveRoomImages: () => void;
  handleCloseDialog: () => void;
}

export function EnlargedImageCarouselUploadDialog({
  handleDelete,
  isOpen,
  picturePaths,
  isLoading,
  files,
  setFiles,
  picturePathsToDelete,
  saveRoomImages,
  handleCloseDialog
}: Readonly<Props>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={isOpen}
      keepMounted
      onClose={handleCloseDialog}
      aria-labelledby="enlarge-image-dialog-label"
      aria-describedby="enlarge-image-dialog-describe"
      maxWidth={"lg"}
      fullWidth={true}
      slotProps={{ paper: { sx: { height: "unset" } } }}
    >
      <DialogTitle data-testid="floorplan-room-images-title">{t("Show Images")}</DialogTitle>

      <DialogContent
        sx={{ display: "flex", height: "65vh", flexDirection: isMobile ? "column" : "row" }}
      >
        <Box
          sx={[
            { alignContent: "center" },
            isMobile ? { width: "100%", mb: 2 } : { flex: 2, pr: 2, minWidth: 0 }
          ]}
        >
          {isOpen ? (
            <EnlargedImageCarousel
              picturePaths={picturePaths}
              handleDelete={handleDelete}
              isLoading={isLoading}
            />
          ) : null}
        </Box>

        <Box sx={isMobile ? { width: "100%" } : { flex: 1 }}>
          <PictureDropzone2
            title={t("_uploadImagesOfSelectedRoom")}
            maxFiles={5}
            files={files}
            onAdd={newFiles => {
              if (newFiles.length) {
                setFiles(prev => [...prev, ...newFiles]);
              }
            }}
            onDelete={(_, index) => setFiles(prev => prev.filter((_, i) => i !== index))}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCloseDialog}
          color={"secondary"}
          data-testid="cancel-enlarged-image-dialog"
        >
          {picturePathsToDelete.length || files.length ? t("Cancel") : t("Close")}
        </Button>

        <Button
          onClick={saveRoomImages}
          color={"primary"}
          data-testid="save-enlarged-image-upload-dialog"
          disabled={files.length === 0 && picturePathsToDelete.length === 0}
        >
          {t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
